/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { TimeZoneEnum } from "@/lib/enum/timezone.enum";

/**
 * Curated fallback list of common IANA timezone identifiers, used only when
 * the runtime does not support `Intl.supportedValuesOf("timeZone")`.
 * Always includes "Europe/Berlin".
 */
const FALLBACK_TIMEZONES: string[] = [
  TimeZoneEnum.utc,
  TimeZoneEnum.europeBerlin,
  "Europe/London",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Vienna",
  "Europe/Zurich",
  "Europe/Warsaw",
  "Europe/Prague",
  "Europe/Stockholm",
  "Europe/Helsinki",
  "Europe/Athens",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Europe/Lisbon",
  "Europe/Dublin",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "America/Mexico_City",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Jerusalem",
  "Australia/Sydney",
  "Australia/Perth",
  "Pacific/Auckland",
  "Africa/Cairo",
  "Africa/Johannesburg"
];

/**
 * Returns the user's IANA timezone as resolved by the browser, falling back
 * to "Europe/Berlin" if the runtime does not report one.
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? TimeZoneEnum.europeBerlin;
}

/**
 * Returns the list of selectable IANA timezone identifiers.
 *
 * Prefers the runtime's full, always-current list via
 * `Intl.supportedValuesOf("timeZone")`; falls back to a curated common set
 * when that API is unavailable. Cast through `any` because the type is not
 * present in the TS lib version used by the webpack-4 toolchain.
 */
export function getTimezones(): string[] {
  const supported = (Intl as any).supportedValuesOf?.("timeZone") as string[] | undefined;

  if (supported && supported.length) {
    return supported;
  }

  return FALLBACK_TIMEZONES;
}
